'use client'

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { getAuthHeaders } from '@/lib/api-utils'
import { can, canAny, canAll, normalizeRole, type Permission, type Role } from '@/lib/rbac'

export interface CurrentUser {
  id: string
  email: string
  login: string
  firstname: string
  lastname: string
  avatar_url?: string
  role: Role
  is_admin: boolean
  must_change_password: boolean
  tenant_id?: string
}

async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const res = await fetch('/api/users/current', { headers: getAuthHeaders() })
  if (!res.ok) return null
  const data = await res.json()
  return data?.user ?? null
}

/**
 * Resolves the signed-in user's org role and exposes RBAC helpers.
 * Backed by react-query so every consumer shares a single request.
 */
export function usePermissions() {
  const { data: user, isLoading } = useQuery({
    queryKey: ['current-user'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000,
  })

  const role = normalizeRole(user?.role)

  return useMemo(
    () => ({
      user: user ?? null,
      role,
      isLoading,
      isAdmin: role === 'admin',
      mustChangePassword: user?.must_change_password === true,
      can: (permission: Permission) => can(role, permission),
      canAny: (permissions: Permission[]) => canAny(role, permissions),
      canAll: (permissions: Permission[]) => canAll(role, permissions),
    }),
    [user, role, isLoading],
  )
}
